/**
 * The one seam between the two demo shells.
 *
 * The web shell calls the vendored `ifc-mesh-wasm` WASM exports directly; the
 * Tauri shell calls the same engine as a Rust `rlib` and ships the identical
 * `{ header, blob }` back over `invoke()`. `mesh_chunk` guarantees its blob is
 * byte-identical to the corresponding chunk of `mesh_buffer_from_ifc`, so the
 * two implementations cannot drift in anything the renderer can observe.
 * Everything downstream of this file is shell-agnostic.
 */

import type {
  AxisLinesTable,
  AxisOnlyTable,
  ElementInfoTable,
  MeshBufferHeader,
  MeshManifest,
  PlanProjectionChunk,
  PlanProjectionChunkHeader,
  PlanProjectionManifest,
  PlanProjectionOpts,
  SessionStats,
  VertexAttribute,
} from "./types";
import {
  IfcEngineError,
  SUPPORTED_MANIFEST_VERSION,
  SUPPORTED_MESH_BUFFER_VERSION,
  toEngineError,
} from "./types";

declare const sessionHandleBrand: unique symbol;

/**
 * An open engine session. The engine hands out a bare `u32`; the brand stops it
 * being confused with a `chunk_id`, which is also a bare `u32`.
 */
export type SessionHandle = number & { readonly [sessionHandleBrand]: true };

/** `mesh_chunk` output before decoding — the exact bytes crossing the seam. */
export interface RawChunk {
  /** `MeshBufferHeader` JSON with a single entry in `chunks`. */
  readonly header: string;
  readonly blob: Uint8Array;
}

/**
 * Which shell — and which linkage — produced the numbers in the diagnostics
 * panel. Three values, not two, because the native shell has two possible
 * linkages and they do not behave the same:
 *
 * - `web-wasm` — browser, WASM in the page.
 * - `tauri-wasm` — Tauri WebView running the *same* WASM build. Bytes reach it
 *   through a native file dialog instead of a `<input type="file">`, but the
 *   engine and its memory limits are the browser's. This is the M4 v1 shell.
 * - `tauri-native` — Tauri linking `ifc-mesh-wasm` as an `rlib`, no WASM
 *   boundary and no linear-memory ceiling. Not implemented yet (design doc §5);
 *   the value exists so a future `TauriMeshSource` cannot quietly report the
 *   WASM shell's numbers.
 *
 * Labelling the linkage is the point: `tauri-wasm` and `tauri-native` are both
 * "the desktop app", and conflating them would let a wasm32 memory limit be read
 * as a native one.
 */
export type MeshSourceShell = "web-wasm" | "tauri-wasm" | "tauri-native";

export interface MeshSource {
  /** Which shell produced the numbers in the diagnostics panel. */
  readonly shell: MeshSourceShell;
  open(bytes: Uint8Array): Promise<SessionHandle>;
  manifest(handle: SessionHandle): Promise<MeshManifest>;
  chunk(handle: SessionHandle, chunkId: number): Promise<RawChunk>;
  stats(handle: SessionHandle): Promise<SessionStats>;
  /**
   * WASM linear-memory size in bytes. The native build has no linear memory and
   * reports 0; do not present it as "the process used 0 bytes".
   */
  linearMemoryBytes(): Promise<number>;
  /** Idempotent. A forgotten handle retains its parsed model AND its plan. */
  close(handle: SessionHandle): Promise<void>;
  /**
   * Dense instanceIndex→element-info table, additive like `guid_table`.
   *
   * Idempotent, call-once, returns the identical table for every call on the
   * same handle. Carries the three shallow attributes (name, ifc_class,
   * description) that the per-chunk mesh buffer specifically excludes.
   * Null counts make absent vs. empty distinguishable (No-Silent-Loss).
   */
  elementInfo(handle: SessionHandle): Promise<ElementInfoTable | null>;
  axisOnly(handle: SessionHandle): Promise<AxisOnlyTable | null>;
  /** Source-authored 2D Axis lines; null when the default-off export is absent. */
  axisLines(handle: SessionHandle): Promise<AxisLinesTable | null>;
  /**
   * Create a source plan projection and return its manifest JSON.
   * The projection is owned by the live session; close it with
   * `closePlanProjection` when done.
   */
  planProjectionManifest(
    handle: SessionHandle,
    opts: PlanProjectionOpts,
  ): Promise<PlanProjectionManifest>;
  /** Fetch one storey chunk of a source plan projection. */
  planProjectionChunk(
    handle: SessionHandle,
    projectionId: number,
    chunkId: number,
  ): Promise<PlanProjectionChunk>;
  /** Drop one source plan projection. Idempotent. */
  closePlanProjection(handle: SessionHandle, projectionId: number): Promise<void>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNonNegativeInteger(value: unknown): value is number {
  return Number.isSafeInteger(value) && (value as number) >= 0;
}

function isPositiveInteger(value: unknown): value is number {
  return Number.isSafeInteger(value) && (value as number) > 0;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

const AXIS_LINES_MAX_POINTS_PER_ENTRY = 4096;
const AXIS_LINES_MAX_TOTAL_POINTS = 65536;
const AXIS_LINE_REFUSAL_REASONS = new Set([
  "source_record_missing",
  "unit_binding_not_closed",
  "storey_binding_not_closed",
  "axis_binding_not_closed",
  "point_budget_exceeded",
  "closed_axis_not_supported",
  "degenerate_axis",
  "non_finite_coordinate",
]);

/** Strictly decode the default-off Axis-lines candidate envelope. */
export function decodeAxisLinesCandidate(raw: unknown): AxisLinesTable {
  if (
    !isRecord(raw) ||
    raw["schema"] !== "ifc-mesh-session-axis-lines/candidate-1" ||
    raw["mesh_buffer_version"] !== 1 ||
    raw["units"] !== "mm" ||
    raw["identity_scope"] !== "live_session_source_record" ||
    !isNonNegativeInteger(raw["source_entry_count"]) ||
    !isNonNegativeInteger(raw["drawable_entry_count"]) ||
    !isNonNegativeInteger(raw["refused_entry_count"]) ||
    !Array.isArray(raw["entries"]) ||
    !Array.isArray(raw["refusals"])
  ) {
    throw new Error("axis-lines candidate schema mismatch");
  }

  if (
    raw["source_entry_count"] !== raw["drawable_entry_count"] + raw["refused_entry_count"] ||
    raw["drawable_entry_count"] !== raw["entries"].length ||
    raw["refused_entry_count"] !== raw["refusals"].length
  ) {
    throw new Error("axis-lines candidate accounting mismatch");
  }

  const identities = new Set<number>();
  let totalPoints = 0;
  const acceptIdentity = (identity: unknown): identity is Record<string, unknown> => {
    if (
      !isRecord(identity) ||
      identity["scope"] !== "live_session_source_record" ||
      !isNonNegativeInteger(identity["express_id"]) ||
      identities.has(identity["express_id"])
    ) {
      return false;
    }
    identities.add(identity["express_id"]);
    return true;
  };

  for (const entry of raw["entries"]) {
    if (
      !isRecord(entry) ||
      !acceptIdentity(entry["identity"]) ||
      typeof entry["ifc_class"] !== "string" ||
      entry["ifc_class"].length === 0 ||
      !isPositiveInteger(entry["storey_level"]) ||
      entry["topology"] !== "open_polyline" ||
      !isFiniteNumber(entry["axis_length_mm"]) ||
      entry["axis_length_mm"] <= 0 ||
      !Array.isArray(entry["polylines_mm"]) ||
      entry["polylines_mm"].length === 0
    ) {
      throw new Error("axis-lines candidate drawable entry mismatch");
    }
    let entryPoints = 0;
    let measuredLength = 0;
    for (const polyline of entry["polylines_mm"]) {
      if (
        !Array.isArray(polyline) ||
        polyline.length < 2 ||
        !polyline.every(
          (point) =>
            Array.isArray(point) &&
            point.length === 2 &&
            isFiniteNumber(point[0]) &&
            isFiniteNumber(point[1]),
        )
      ) {
        throw new Error("axis-lines candidate polyline mismatch");
      }
      const first = polyline[0] as readonly [number, number];
      const last = polyline[polyline.length - 1] as readonly [number, number];
      if (first[0] === last[0] && first[1] === last[1]) {
        throw new Error("axis-lines candidate topology mismatch");
      }
      entryPoints += polyline.length;
      for (let i = 1; i < polyline.length; i += 1) {
        const previous = polyline[i - 1] as readonly [number, number];
        const current = polyline[i] as readonly [number, number];
        const segmentLength = Math.hypot(current[0] - previous[0], current[1] - previous[1]);
        if (!Number.isFinite(segmentLength)) {
          throw new Error("axis-lines candidate derived length is non-finite");
        }
        measuredLength += segmentLength;
        if (!Number.isFinite(measuredLength)) {
          throw new Error("axis-lines candidate derived length is non-finite");
        }
      }
    }
    totalPoints += entryPoints;
    if (
      entryPoints > AXIS_LINES_MAX_POINTS_PER_ENTRY ||
      totalPoints > AXIS_LINES_MAX_TOTAL_POINTS
    ) {
      throw new Error("axis-lines candidate point budget exceeded");
    }
    const lengthTolerance = Math.max(1e-6, measuredLength * 1e-9);
    if (Math.abs(entry["axis_length_mm"] - measuredLength) > lengthTolerance) {
      throw new Error("axis-lines candidate length mismatch");
    }
  }

  for (const refusal of raw["refusals"]) {
    if (
      !isRecord(refusal) ||
      !acceptIdentity(refusal["identity"]) ||
      typeof refusal["ifc_class"] !== "string" ||
      refusal["ifc_class"].length === 0 ||
      typeof refusal["reason"] !== "string" ||
      !AXIS_LINE_REFUSAL_REASONS.has(refusal["reason"])
    ) {
      throw new Error("axis-lines candidate refusal mismatch");
    }
  }

  return raw as unknown as AxisLinesTable;
}

/** Ensure the complete C ledger and the drawable/refused B ledger describe the same source rows. */
export function assertAxisLedgerAlignment(
  axisOnly: AxisOnlyTable | null,
  axisLines: AxisLinesTable | null,
): void {
  if (axisOnly === null && axisLines === null) {
    return;
  }
  if (axisOnly === null || axisLines === null) {
    throw new Error("axis-only and axis-lines candidate availability mismatch");
  }
  if (
    axisOnly.schema !== "ifc-mesh-session-axis-only/candidate-1" ||
    axisOnly.mesh_buffer_version !== 1 ||
    axisOnly.entries.some(
      (entry) =>
        !isNonNegativeInteger(entry.express_id) ||
        typeof entry.ifc_class !== "string" ||
        entry.ifc_class.length === 0,
    )
  ) {
    throw new Error("axis-only candidate schema mismatch");
  }
  const expected = new Map(axisOnly.entries.map((entry) => [entry.express_id, entry.ifc_class]));
  if (axisOnly.entry_count !== axisOnly.entries.length || expected.size !== axisOnly.entries.length) {
    throw new Error("axis-only candidate accounting mismatch");
  }
  const actual = [...axisLines.entries, ...axisLines.refusals];
  if (
    axisLines.source_entry_count !== expected.size ||
    actual.some((entry) => expected.get(entry.identity.express_id) !== entry.ifc_class)
  ) {
    throw new Error("axis-only and axis-lines candidate ledgers do not align");
  }
}

/**
 * Marker used to drop the repeated `guid_table` before `JSON.parse` runs.
 *
 * `serde` emits struct fields in declaration order, so `guid_table` is the last
 * key of `MeshBufferHeader`. Every string that precedes it is a compile-time
 * constant (`units`, `determinism`, attribute names) — no model-derived text can
 * contain this marker, and `lastIndexOf` guards the remaining theoretical case.
 * If the shape ever changes the check below fails and we fall back to a full
 * parse, so this is a cost optimisation and never a correctness fork.
 */
const GUID_TABLE_MARKER = ',"guid_table":';

/**
 * Exact opening bytes of a v1 header in `serde_json` compact output (no spaces,
 * fields in declaration order, `mesh_buffer_version` first).
 *
 * Gating the strip on this prefix runs the version check BEFORE the optimisation
 * rather than after it, and makes an unrecognised header fall back to a full
 * parse instead of being mutilated first and rejected second.
 */
const V1_HEADER_PREFIX = '{"mesh_buffer_version":1,';

/**
 * Expected v1 vertex-layout attributes, `(name, type, count, offset)`, per
 * `VertexLayout::v1()` (`header.rs:90-115`) and the README's "How the renderer
 * works" table. `renderer.ts` hard-codes these offsets/types when it builds
 * the `InterleavedBuffer` views, so a future layout that keeps `stride_bytes
 * === 12` while moving or retyping an attribute must fail loudly here instead
 * of decoding silently wrong.
 */
const V1_VERTEX_ATTRIBUTES: readonly VertexAttribute[] = [
  { name: "position", type: "u16", count: 3, offset: 0 },
  { name: "normal_oct", type: "i8", count: 2, offset: 6 },
  { name: "instanceIndex", type: "u32", count: 1, offset: 8 },
];

function sameVertexAttributes(
  actual: readonly VertexAttribute[],
  expected: readonly VertexAttribute[],
): boolean {
  if (actual.length !== expected.length) {
    return false;
  }
  return expected.every((exp) =>
    actual.some(
      (attr) =>
        attr.name === exp.name &&
        attr.type === exp.type &&
        attr.count === exp.count &&
        attr.offset === exp.offset,
    ),
  );
}

export interface DecodedChunkHeader {
  readonly header: MeshBufferHeader;
  /**
   * True when the repeated `guid_table` was removed at the string level, false
   * when the full JSON (table included) was parsed. Exposed because the whole
   * point of the strip is a consumer-side measurement: a silent fallback to the
   * slow path must not be reported as if the optimisation were active.
   */
  readonly guidTableStripped: boolean;
}

/**
 * Both `manifest::to_json` and `header_to_json` are infallible on the Rust side:
 * a serialisation failure returns `{"error": "..."}` as a *successful* string.
 * Detect that before the version gate, or the engine's own words get replaced by
 * a bogus "unsupported version undefined".
 */
function engineErrorEnvelope(value: unknown): string | null {
  if (typeof value === "object" && value !== null) {
    const inner: unknown = (value as Record<string, unknown>)["error"];
    if (typeof inner === "string") {
      return inner;
    }
  }
  return null;
}

/**
 * Decode a chunk header, optionally without materialising `guid_table`.
 *
 * The table is the whole model's and is repeated on every chunk, so parsing it
 * per chunk is O(chunks x products). The demo keeps the first chunk's table and
 * strips the rest at the string level — the wire is untouched, the saving is
 * entirely consumer-side.
 */
export function decodeChunkHeader(json: string, keepGuidTable: boolean): DecodedChunkHeader {
  let source = json;
  let guidTableStripped = false;

  if (!keepGuidTable && json.startsWith(V1_HEADER_PREFIX)) {
    const marker = json.lastIndexOf(GUID_TABLE_MARKER);
    if (marker > 0 && json.endsWith("]}")) {
      source = `${json.slice(0, marker)},"guid_table":[]}`;
      guidTableStripped = true;
    }
  }

  const header = JSON.parse(source) as MeshBufferHeader;
  const envelope = engineErrorEnvelope(header);
  if (envelope !== null) {
    throw new IfcEngineError("mesh_chunk", envelope, source);
  }
  if (header.mesh_buffer_version !== SUPPORTED_MESH_BUFFER_VERSION) {
    throw new Error(
      `unsupported mesh_buffer_version ${header.mesh_buffer_version} ` +
        `(this viewer decodes v${SUPPORTED_MESH_BUFFER_VERSION})`,
    );
  }
  if (header.vertex_layout.stride_bytes !== 12 || !header.vertex_layout.interleaved) {
    throw new Error(
      `unsupported vertex layout: interleaved=${String(header.vertex_layout.interleaved)} ` +
        `stride=${header.vertex_layout.stride_bytes}`,
    );
  }
  if (!sameVertexAttributes(header.vertex_layout.attributes, V1_VERTEX_ATTRIBUTES)) {
    throw new Error(
      `unsupported vertex layout: attributes ${JSON.stringify(header.vertex_layout.attributes)} ` +
        "do not match the v1 contract (position u16x3@0, normal_oct i8x2@6, instanceIndex u32x1@8)",
    );
  }
  return { header, guidTableStripped };
}

function decodeManifest(json: string): MeshManifest {
  const manifest = JSON.parse(json) as MeshManifest;
  const envelope = engineErrorEnvelope(manifest);
  if (envelope !== null) {
    throw new IfcEngineError("mesh_manifest", envelope, json);
  }
  if (manifest.manifest_version !== SUPPORTED_MANIFEST_VERSION) {
    throw new Error(
      `unsupported manifest_version ${manifest.manifest_version} ` +
        `(this viewer reads v${SUPPORTED_MANIFEST_VERSION})`,
    );
  }
  return manifest;
}

/**
 * The vendored WASM package, resolved through the `ifc-mesh-wasm` alias in
 * `vite.config.ts` / `tsconfig.json`. It is a build output of
 * `wasm-pack build --target web --features wasm,mesh-element-info-candidate`, not a checked-in file.
 */
type WasmModule = typeof import("ifc-mesh-wasm");

let wasmModule: Promise<WasmModule> | null = null;

/** Instantiate the WASM module once per page; concurrent callers share it. */
function loadWasm(): Promise<WasmModule> {
  if (wasmModule === null) {
    wasmModule = (async () => {
      const mod = await import("ifc-mesh-wasm");
      // `--target web` exports the initialiser as the module default. Called
      // without arguments it fetches `ifc_mesh_wasm_bg.wasm` relative to the
      // glue module, which Vite rewrites to the emitted asset URL.
      await mod.default();
      return mod;
    })().catch((cause: unknown) => {
      // Do not cache a failed instantiation: a reload-free retry must be possible.
      wasmModule = null;
      throw toEngineError("wasm_init", cause);
    });
  }
  return wasmModule;
}

export class WasmMeshSource implements MeshSource {
  /**
   * Declared as the wide union, not the literal, so a shell that reuses this
   * class over a different transport can override the label. The Tauri v1 shell
   * does exactly that: same WASM, different way of getting bytes.
   */
  readonly shell: MeshSourceShell = "web-wasm";

  /** Warm the module before the user picks a file, so the first open is not gated on the fetch. */
  async preload(): Promise<void> {
    await loadWasm();
  }

  async open(bytes: Uint8Array): Promise<SessionHandle> {
    const wasm = await loadWasm();
    try {
      return wasm.open_ifc(bytes) as SessionHandle;
    } catch (cause: unknown) {
      throw toEngineError("open_ifc", cause);
    }
  }

  async manifest(handle: SessionHandle): Promise<MeshManifest> {
    const wasm = await loadWasm();
    try {
      return decodeManifest(wasm.mesh_manifest(handle));
    } catch (cause: unknown) {
      throw toEngineError("mesh_manifest", cause);
    }
  }

  async chunk(handle: SessionHandle, chunkId: number): Promise<RawChunk> {
    const wasm = await loadWasm();
    try {
      const result = wasm.mesh_chunk(handle, chunkId);
      try {
        // Both getters clone out of linear memory on EVERY access. Read each
        // exactly once, then free the handle so the WASM-side copy is dropped.
        const header = result.header;
        const blob = result.blob;
        return { header, blob };
      } finally {
        result.free();
      }
    } catch (cause: unknown) {
      throw toEngineError("mesh_chunk", cause);
    }
  }

  async stats(handle: SessionHandle): Promise<SessionStats> {
    const wasm = await loadWasm();
    try {
      return JSON.parse(wasm.mesh_session_stats(handle)) as SessionStats;
    } catch (cause: unknown) {
      throw toEngineError("mesh_session_stats", cause);
    }
  }

  async linearMemoryBytes(): Promise<number> {
    const wasm = await loadWasm();
    return wasm.mesh_linear_memory_bytes();
  }

  async close(handle: SessionHandle): Promise<void> {
    const wasm = await loadWasm();
    // Documented as never throwing: an unknown or already-closed handle is a no-op.
    wasm.close_ifc(handle);
  }

  async elementInfo(handle: SessionHandle): Promise<ElementInfoTable | null> {
    const wasm = await loadWasm();
    // The candidate export exists only when the WASM was built with the
    // mesh-element-info-candidate feature. An absent export means the engine
    // build excludes the candidate (stable release cycle, not demo work).
    if (typeof (wasm as Record<string, unknown>)["mesh_session_element_info_candidate"] !== "function") {
      return null;
    }
    try {
      return JSON.parse(
        wasm.mesh_session_element_info_candidate(handle),
      ) as ElementInfoTable;
    } catch (cause: unknown) {
      throw toEngineError("mesh_session_element_info_candidate", cause);
    }
  }

  async axisOnly(handle: SessionHandle): Promise<AxisOnlyTable | null> {
    const wasm = await loadWasm();
    // Same candidate gating as elementInfo: an absent export means the build
    // excludes the mesh-axis-only-candidate feature.
    if (typeof (wasm as Record<string, unknown>)["mesh_session_axis_only_candidate"] !== "function") {
      return null;
    }
    try {
      return JSON.parse(
        wasm.mesh_session_axis_only_candidate(handle),
      ) as AxisOnlyTable;
    } catch (cause: unknown) {
      throw toEngineError("mesh_session_axis_only_candidate", cause);
    }
  }

  async axisLines(handle: SessionHandle): Promise<AxisLinesTable | null> {
    const wasm = await loadWasm();
    const exportFn = (wasm as Record<string, unknown>)["mesh_session_axis_lines_candidate"];
    if (typeof exportFn !== "function") {
      return null;
    }
    try {
      const json = (exportFn as (sessionHandle: number) => string)(handle);
      return decodeAxisLinesCandidate(JSON.parse(json));
    } catch (cause: unknown) {
      throw toEngineError("mesh_session_axis_lines_candidate", cause);
    }
  }

  async planProjectionManifest(
    handle: SessionHandle,
    opts: PlanProjectionOpts,
  ): Promise<PlanProjectionManifest> {
    const wasm = await loadWasm();
    try {
      const json = wasm.source_plan_projection_manifest(handle, JSON.stringify(opts));
      return JSON.parse(json) as PlanProjectionManifest;
    } catch (cause: unknown) {
      throw toEngineError("source_plan_projection_manifest", cause);
    }
  }

  async planProjectionChunk(
    handle: SessionHandle,
    projectionId: number,
    chunkId: number,
  ): Promise<PlanProjectionChunk> {
    const wasm = await loadWasm();
    try {
      const result = wasm.source_plan_projection_chunk(handle, BigInt(projectionId), chunkId);
      try {
        const header = result.header;
        const blob = result.blob;
        return {
          header: JSON.parse(header) as PlanProjectionChunkHeader,
          blob,
        };
      } finally {
        result.free();
      }
    } catch (cause: unknown) {
      throw toEngineError("source_plan_projection_chunk", cause);
    }
  }

  async closePlanProjection(handle: SessionHandle, projectionId: number): Promise<void> {
    const wasm = await loadWasm();
    try {
      wasm.close_source_plan_projection(handle, BigInt(projectionId));
    } catch (cause: unknown) {
      throw toEngineError("close_source_plan_projection", cause);
    }
  }
}

/*
 * The M4 v1 Tauri shell does NOT implement a second `MeshSource`. It runs this
 * one inside the WebView (`shell = "tauri-wasm"`, see
 * `demo/tauri/src/mesh-source.ts`) and changes only where the bytes come from —
 * a native dialog instead of a file input. One engine build, one code path, so
 * there is nothing for the two shells to drift on in v1.
 *
 * TODO(M4+, design doc §5): `TauriMeshSource implements MeshSource` with
 * `shell = "tauri-native"`, linking `ifc-mesh-wasm` as an `rlib` and returning
 * the same `{ header, blob }` bytes over `invoke()`. It belongs to the Tauri
 * shell package, not here, because it pulls in `@tauri-apps/api` — a dependency
 * the static web build must not carry. `mesh_chunk` guarantees byte-identical
 * blobs, so nothing below this interface changes when it lands. That is also the
 * seam the A' dogfood needs, which is why it is worth building later rather than
 * never.
 *
 * TODO(M5): the A' face-record path is Tauri-only, and only over that native
 * linkage. The web shell cannot have it: consuming records in the browser would
 * require a Layer B WASM export, which is under HOLD. The seam runs on wasm32 —
 * the reason is contractual, not technical.
 */
